import { Alert, Button, Modal } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useActiveSession } from '../api/active/sessionProvider';
import { useInventoryReadRuntime } from '../inventory-integration/inventoryPageRuntime';
import styles from './MarketingAnnouncement.module.css';
import { MarketingAnnouncementContent } from './MarketingAnnouncementContent';
import {
  type AnnouncementReadState,
  useOptionalMarketingAnnouncementPrefetch,
} from './MarketingAnnouncementPrefetch';
import {
  createMarketingAnnouncementApi,
  hasAnnouncementContent,
} from './marketingAnnouncementApi';

export function MarketingAnnouncementDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose(): void;
}) {
  const session = useActiveSession();
  const inventory = useInventoryReadRuntime();
  const prefetch = useOptionalMarketingAnnouncementPrefetch();
  const api = useMemo(
    () => createMarketingAnnouncementApi(session.transport, null),
    [session.transport],
  );
  const [localRead, setLocalRead] = useState<AnnouncementReadState>({
    status: 'loading',
  });
  const read = prefetch?.read ?? localRead;
  const [revision, setRevision] = useState(0);
  const requestedInventoryIds = useRef(new Set<string>());
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const openedOnce = useRef(false);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (!open) {
      wasOpen.current = false;
      return;
    }
    if (wasOpen.current) return;
    wasOpen.current = true;
    requestedInventoryIds.current.clear();
    if (openedOnce.current) prefetch?.reload();
    openedOnce.current = true;
  }, [open, prefetch]);

  useEffect(() => {
    if (!open || prefetch) return;
    const controller = new AbortController();
    setLocalRead({ status: 'loading' });
    api.get(controller.signal).then(
      (value) => {
        if (controller.signal.aborted) return;
        setLocalRead({ status: 'ready', value });
      },
      (cause) => {
        if (!controller.signal.aborted)
          setLocalRead({
            status: 'error',
            message:
              cause instanceof Error ? cause.message : '营销说明读取失败',
          });
      },
    );
    return () => controller.abort();
  }, [api, open, prefetch, revision]);

  useEffect(() => {
    if (open && read.status === 'ready' && !hasAnnouncementContent(read.value))
      closeRef.current();
  }, [open, read]);

  const announcement = read.status === 'ready' ? read.value : null;
  const loadDevices = inventory.loadDevices;
  const canReadInventory = inventory.canRead;
  const loadVisibleDevices = useCallback(
    (ids: string[]) => {
      if (!canReadInventory) return;
      const fresh = ids.filter((id) => !requestedInventoryIds.current.has(id));
      for (const id of fresh) requestedInventoryIds.current.add(id);
      if (fresh.length) void loadDevices(fresh, { force: true });
    },
    [canReadInventory, loadDevices],
  );

  if (!open || (announcement && !hasAnnouncementContent(announcement)))
    return null;
  return (
    <Modal
      title="营销说明"
      open
      onCancel={onClose}
      mask={{ closable: false }}
      keyboard={false}
      footer={null}
      width="min(94vw, 1500px)"
      style={{ top: 24 }}
      className={styles.modal}
    >
      {read.status === 'loading' ? (
        <div role="status" className={styles.announcementLoading}>
          正在准备营销说明…
        </div>
      ) : read.status === 'error' ? (
        <Alert
          type="error"
          showIcon
          title="营销说明读取失败"
          description={read.message}
          action={
            <Button
              size="small"
              onClick={() =>
                prefetch ? prefetch.reload() : setRevision((value) => value + 1)
              }
            >
              重试
            </Button>
          }
        />
      ) : (
        <MarketingAnnouncementContent
          announcement={read.value}
          inventoryById={
            new Map(
              [...inventory.devices.byId].filter(
                ([id]) =>
                  requestedInventoryIds.current.has(id) &&
                  inventory.devices.readStateById?.get(id)?.status === 'ready',
              ),
            )
          }
          inventoryReadStateById={inventory.devices.readStateById}
          deviceLabels={new Map(Object.entries(read.value.deviceLabels ?? {}))}
          canReadInventory={canReadInventory}
          onVisibleDevices={loadVisibleDevices}
        />
      )}
    </Modal>
  );
}
